import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";
import _ from "lodash";
import moment from "moment";
import { withAlert } from "react-alert";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import UserStore from "../../utils/UserStore";
import Group from "../../waybee-ui/Group";
import BackButton from "../../components/BackButton";
import TeacherService from "../../services/TeacherService";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import { isJobInProgress } from "../../utils/jobHelpers";
import { clearLeaveGuard, setLeaveGuard } from "../../utils/leaveGuard";
import { lastTitleSegment } from "../../utils/breadcrumbs";
import { cachedClassName, ensureClassName } from "../../utils/classDirectory";
import JobInProgressLoading from "../../components/JobInProgressLoading";
import Button from "../../waybee-ui/Button";
import Dialog from "../../waybee-ui/Dialog";
import DialogTitle from "../../waybee-ui/Dialog/DialogTitle";
import DialogContent from "../../waybee-ui/Dialog/DialogContent";
import DialogActions from "../../waybee-ui/Dialog/DialogActions";
import { AbilityContext, Can } from "../../acl/can";
import {
  formatLessonLabel,
  occurrenceMatchesLetter,
  REFO_LETTER_COLORS,
  mergeRefoCatalog,
  studentLetterMarks
} from "./refoLetters";

const LoadContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 8px;
`;

const StickyScope = styled.div`
  position: relative;
`;

const StickySentinel = styled.div`
  height: 1px;
  margin-bottom: -1px;
  pointer-events: none;
`;

const StickyPlaceholder = styled.div`
  height: ${props => props.$height || 0}px;
`;

const StickyChrome = styled.div`
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  ${props =>
    props.$stuck &&
    props.$box &&
    `
    position: fixed;
    top: 0;
    left: ${props.$box.left}px;
    width: ${props.$box.width}px;
    z-index: 1200;
  `}
`;

const Breadcrumb = styled.nav`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #555;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CrumbLink = styled(Link)`
  color: ${({ theme }) => theme.color.secondary.main};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CrumbSep = styled.span`
  color: #aaa;
`;

const CrumbCurrent = styled.span`
  color: #222;
  font-weight: 600;
`;

const CompactSave = styled(Button)`
  && {
    min-width: 120px;
    height: 40px;
    flex-shrink: 0;
  }
`;

const LessonFilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  padding: 4px 16px 12px;
`;

const FilterFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 40px;
  flex: 1 1 auto;
  min-width: 0;
`;

const DatePickerField = styled(KeyboardDatePicker)`
  margin-top: 0;
  margin-bottom: 0;
  width: 200px;
  flex-shrink: 0;
`;

const LessonField = styled(FormControl)`
  flex: 1 1 360px;
  min-width: 280px;
  max-width: 520px;
`;

const LegendBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: #161616;
`;

const LegendItem = styled.div`
  flex: 1 1 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 10px 12px;
  border-left: 1px solid #2c2c2c;
  color: #eee;
  font-size: 12px;
  line-height: 1.3;
  text-align: center;

  &:first-child {
    border-left: 0;
  }
`;

const LegendLetter = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 3px;
  background: ${props => props.$color || "#666"};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const Album = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const StudentCard = styled.div`
  width: 236px;
  margin: 5px 17px 16px;
`;

const PhotoRow = styled.div`
  display: flex;
  height: 198px;
`;

const Portrait = styled.div`
  width: 168px;
  height: 198px;
  border: 1px solid #666;
  background: #e8e8e8;
  overflow: hidden;
  flex-shrink: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: ${props => (props.$clickable ? "pointer" : "default")};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
  }
`;

const Initials = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 42px;
  font-weight: 500;
  color: #666;
  background: #d5d5d5;
`;

const LetterCol = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 68px;
  height: 198px;
  margin: 0;
  padding: 0;
  list-style: none;
  background: #666;
  flex-shrink: 0;

  li {
    flex: 1 1 0;
    min-height: 0;
  }
`;

const LetterButton = styled.button`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  font-size: 18px;
  line-height: 1;
  text-align: center;
  cursor: ${props => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${props => (props.$unavailable || props.disabled ? 0.35 : 1)};
  background: ${props => (props.$active ? props.$color || "#ddd" : "#48484b")};
  color: #fff;
`;

const StudentRa = styled.span`
  display: block;
  margin-top: 6px;
  text-align: center;
  color: #888;
  letter-spacing: 0.03em;
  font-size: 12px;
`;

const StudentName = styled.span`
  display: block;
  text-align: center;
  font-size: 13px;
  line-height: 1.25;
`;

function toLessonQueryDate(value) {
  if (!value) return null;
  const day = moment.utc(value);
  if (!day.isValid()) return null;
  return `${day.format("YYYY-MM-DD")}T12:00:00`;
}

function defaultRefoLessonDate() {
  const ano = Number(UserStore.getAnoLetivo());
  const today = moment();
  if (!ano || today.year() === ano) {
    return today.format("YYYY-MM-DDTHH:mm:ss");
  }
  return toLessonQueryDate(`${ano}-12-19`);
}

const LETTER_LABEL_KEYS = {
  A: "occurrences.refo.letters.A",
  B: "occurrences.refo.letters.B",
  C: "occurrences.refo.letters.C",
  D: "occurrences.refo.letters.D",
  E: "occurrences.refo.letters.E",
  F: "occurrences.refo.letters.F"
};

class RefoGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      classId:
        (props.match && props.match.params && props.match.params.id) || null,
      filter: { lesson: "" },
      selectedLessonDate: defaultRefoLessonDate(),
      loadingSave: false,
      loadingSchedules: true,
      schedulesError: false,
      loadingOccurrences: false,
      schedules: [],
      letters: [],
      students: [],
      marks: {},
      dirty: false,
      loadingJobs: [],
      processedJobs: [],
      loadingMessage: "",
      detailLetter: null,
      detailStudent: null,
      detailType: "",
      detailDescription: "",
      chromeStuck: false,
      chromeHeight: 0,
      chromeBox: null,
      className: ""
    };
  }

  componentDidMount() {
    this.getSchedules();
    this.loadClassName();
    window.addEventListener("resize", this.syncChromeBox);
  }

  componentDidUpdate(prevProps) {
    const { dirty } = this.state;
    setLeaveGuard(() => dirty);
    this.checkOccurrenceJobCompletion(prevProps);
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearLeaveGuard();
    window.removeEventListener("resize", this.syncChromeBox);
    if (this.stickObserver) {
      this.stickObserver.disconnect();
      this.stickObserver = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  setSentinelRef = el => {
    this.stickySentinel = el;
    if (this.stickObserver) {
      this.stickObserver.disconnect();
      this.stickObserver = null;
    }
    if (!el || typeof IntersectionObserver === "undefined") return;
    this.stickObserver = new IntersectionObserver(
      ([entry]) => {
        const stuck = entry.boundingClientRect.top < 0;
        this.setState(prev => {
          if (prev.chromeStuck === stuck) return null;
          if (stuck && this.chromeEl) {
            const rect = this.chromeEl.getBoundingClientRect();
            return {
              chromeStuck: true,
              chromeBox: { left: rect.left, width: rect.width },
              chromeHeight: this.chromeEl.offsetHeight
            };
          }
          return { chromeStuck: false, chromeBox: null };
        });
      },
      { threshold: [0, 1] }
    );
    this.stickObserver.observe(el);
  };

  setChromeRef = el => {
    this.chromeEl = el;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (!el || typeof ResizeObserver === "undefined") return;
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.chromeEl) return;
      const height = this.chromeEl.offsetHeight;
      this.setState(prev =>
        prev.chromeHeight === height ? null : { chromeHeight: height }
      );
    });
    this.resizeObserver.observe(el);
  };

  syncChromeBox = () => {
    const { chromeStuck } = this.state;
    if (!chromeStuck || !this.stickySentinel) return;
    const parent = this.stickySentinel.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    this.setState(prev => ({
      chromeBox: { left: rect.left, width: rect.width },
      chromeHeight: prev.chromeHeight
    }));
  };

  getSelectedSchedule = () => {
    const { filter, schedules } = this.state;
    return schedules.find(schedule => schedule.id === filter.lesson);
  };

  formatSelectedDate = schedule => {
    const [hour, minute] = schedule.startTime.split(":");
    return moment(schedule.date)
      .set({ hour, minute })
      .format("YYYY-MM-DD HH:mm:SS");
  };

  formatLessonOption = lesson => formatLessonLabel(lesson);

  studentInitials = name => {
    return String(name || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0])
      .join("")
      .toUpperCase();
  };

  getPendingOccurrenceJobKey = (classId, schedule) => {
    if (!classId || !schedule || !schedule.idTurmaDisc) {
      return null;
    }
    const date = this.formatSelectedDate(schedule);
    if (!date) {
      return null;
    }
    return `${classId}${schedule.idTurmaDisc}${date}`;
  };

  matchesPendingOccurrenceJob = (job, classId, schedule) => {
    if (!job || !classId || !schedule) {
      return false;
    }
    const date = this.formatSelectedDate(schedule);
    return (
      job.name === "save_occurrences" &&
      job.classId === classId &&
      String(job.course) === String(schedule.idTurmaDisc) &&
      job.period === date
    );
  };

  handleOccurrenceJobCompleted = (jobId, pendingJobKey, schedule) => {
    const { jobsState } = this.props;
    const { classId } = this.state;

    const stillPending = jobsState.jobs.some(
      job =>
        job.id !== jobId &&
        isJobInProgress(job) &&
        this.matchesPendingOccurrenceJob(job, classId, schedule)
    );

    if (stillPending) {
      this.setState(prev => ({
        processedJobs: [...prev.processedJobs, jobId]
      }));
      return;
    }

    this.setState(
      prev => ({
        loadingJobs: prev.loadingJobs.filter(
          jobKey => jobKey !== pendingJobKey
        ),
        processedJobs: [...prev.processedJobs, jobId]
      }),
      () => {
        if (schedule) {
          this.loadStudents(schedule);
        }
      }
    );
  };

  checkOccurrenceJobCompletion = prevProps => {
    const {
      jobsState: { jobs }
    } = this.props;
    const { classId, loadingJobs, processedJobs } = this.state;
    const schedule = this.getSelectedSchedule();
    const pendingJobKey = this.getPendingOccurrenceJobKey(classId, schedule);

    if (!pendingJobKey || loadingJobs.length === 0 || jobs.length < 1) {
      return;
    }

    jobs.forEach(job => {
      const previousJob = prevProps.jobsState.jobs.find(
        prevJob => prevJob.id === job.id
      );
      const hasProcessed = processedJobs.includes(job.id);

      if (
        !hasProcessed &&
        (job.status === "success" || job.status === "error") &&
        previousJob &&
        job.status !== previousJob.status &&
        this.matchesPendingOccurrenceJob(job, classId, schedule)
      ) {
        this.handleOccurrenceJobCompleted(job.id, pendingJobKey, schedule);
      }
    });
  };

  syncOccurrenceJobQueue = () => {
    const { jobsState, t } = this.props;
    const { classId, loadingJobs } = this.state;
    const schedule = this.getSelectedSchedule();
    const pendingJobKey = this.getPendingOccurrenceJobKey(classId, schedule);

    if (!pendingJobKey || !jobsState || !jobsState.jobs) {
      return;
    }

    jobsState.jobs.forEach(job => {
      if (
        isJobInProgress(job) &&
        this.matchesPendingOccurrenceJob(job, classId, schedule) &&
        !loadingJobs.includes(pendingJobKey)
      ) {
        this.setState(prevState => ({
          loadingJobs: [...prevState.loadingJobs, pendingJobKey],
          loadingMessage: t("occurrences.refo.jobInProgress")
        }));
      }
    });
  };

  pickDefaultSchedule = schedules => {
    if (!schedules.length) return null;
    return schedules[0];
  };

  handleScheduleDateChange = async date => {
    if (!date || !moment(date).isValid()) return;
    const nextDate = toLessonQueryDate(date);
    if (!nextDate) return;
    await this.setState({
      selectedLessonDate: nextDate
    });
    this.getSchedules();
  };

  bootstrapLesson = async schedule => {
    try {
      const letters = await this.getOccurrenceTypes(schedule.idTurmaDisc);
      await this.loadStudents(schedule, letters);
    } catch (e) {
      console.error(e);
      const { letters } = this.state;
      await this.loadStudents(schedule, letters);
    }
  };

  loadClassName = async () => {
    const { match } = this.props;
    const classId = match.params.id;
    this.setState({ className: cachedClassName(classId) || classId });
    const className = await ensureClassName(classId);
    if (!this.unmounted) this.setState({ className });
  };

  getSchedules = async () => {
    this.setState({ loadingSchedules: true, schedulesError: false });
    const { selectedLessonDate } = this.state;
    const { match, alert, t } = this.props;
    const classId = match.params.id;

    try {
      const schedules = await TeacherService.getScheduleForOccurrence(classId, {
        date: selectedLessonDate
      });
      const defaultSchedule = this.pickDefaultSchedule(schedules);
      this.setState(
        {
          schedules,
          classId,
          loadingSchedules: false,
          filter: { lesson: defaultSchedule ? defaultSchedule.id : "" },
          ...(defaultSchedule ? {} : { students: [], marks: {} })
        },
        () => {
          if (defaultSchedule) {
            this.bootstrapLesson(defaultSchedule);
          }
        }
      );
    } catch (e) {
      console.error(e);
      this.setState({ classId, loadingSchedules: false, schedulesError: true });
      alert.show(t("occurrences.error.schedules"), {
        title: t("common.actions.error")
      });
    }
  };

  getOccurrenceTypes = async idTurmaDisc => {
    const classId = localStorage.getItem("class");
    const occurrenceTypes = await TeacherService.getOccurrenceTypes(
      classId,
      idTurmaDisc
    );
    const letters = mergeRefoCatalog(occurrenceTypes);
    this.setState({ letters });
    return letters;
  };

  loadStudents = async (schedule, lettersOverride) => {
    const { alert, t } = this.props;
    this.setState({ loadingOccurrences: true });

    try {
      const students = await TeacherService.getOccurrence(
        schedule.idTurmaDisc,
        this.formatSelectedDate(schedule),
        schedule.scheduleId,
        schedule.weekDay
      );
      const { letters: stateLetters } = this.state;
      const letters = lettersOverride || stateLetters;
      const marks = {};
      students.forEach(student => {
        marks[student.id] = studentLetterMarks(student, letters);
      });
      this.setState(
        {
          students,
          marks,
          dirty: false,
          loadingOccurrences: false
        },
        () => {
          this.syncOccurrenceJobQueue();
        }
      );
    } catch (e) {
      console.error(e);
      this.setState({
        loadingOccurrences: false,
        students: [],
        marks: {}
      });
      alert.show(t("occurrences.error.schedules"), {
        title: t("common.actions.error")
      });
    }
  };

  changeSchedule = lessonId => {
    const { schedules } = this.state;
    const schedule = schedules.find(item => item.id === lessonId);
    if (schedule) {
      this.bootstrapLesson(schedule);
    }
  };

  isJobPending = () => {
    const { classId, loadingJobs } = this.state;
    const schedule = this.getSelectedSchedule();
    const pendingJobKey = this.getPendingOccurrenceJobKey(classId, schedule);
    return !!pendingJobKey && loadingJobs.includes(pendingJobKey);
  };

  openStudentProfile = student => {
    const { context: ability } = this;
    const { classId } = this.state;
    const { match, history } = this.props;
    if (!ability || !ability.can("use module", "diarySpecificStudent")) return;
    const ra = student.ra || student.id;
    if (!ra) return;
    const resolvedClassId =
      classId || (match && match.params && match.params.id);
    if (resolvedClassId) localStorage.setItem("class", resolvedClassId);
    history.push(`/student/${encodeURIComponent(ra)}`);
  };

  toggleLetter = (student, letter) => {
    if (this.isJobPending() || letter.kind === "unavailable") return;

    const { marks } = this.state;
    if (letter.kind === "occurrence" && letter.options) {
      const current = (marks[student.id] || {})[letter.letter];
      const first = letter.options[0];
      this.setState({
        detailLetter: letter,
        detailStudent: student,
        detailType: current
          ? `${current.idType}-${current.idGroup}`
          : `${first.idType}-${first.idGroup}`,
        detailDescription: current ? current.description || "" : ""
      });
      return;
    }

    const current = marks[student.id] || {};
    const already = current[letter.letter];
    if (letter.kind === "absence") {
      this.setMark(student.id, letter.letter, !already);
      return;
    }

    this.setMark(
      student.id,
      letter.letter,
      already
        ? false
        : {
            idGroup: letter.idGroup,
            idType: letter.idType,
            description: ""
          }
    );
  };

  setMark = (studentId, letter, value) => {
    this.setState(prev => ({
      dirty: true,
      marks: {
        ...prev.marks,
        [studentId]: {
          ...(prev.marks[studentId] || {}),
          [letter]: value
        }
      }
    }));
  };

  confirmDetail = () => {
    const {
      detailLetter,
      detailStudent,
      detailType,
      detailDescription,
      letters
    } = this.state;
    if (!detailLetter || !detailStudent) return;
    const option = (detailLetter.options || []).find(
      item => `${item.idType}-${item.idGroup}` === detailType
    );
    if (!option) return;
    this.setMark(detailStudent.id, detailLetter.letter, {
      idGroup: option.idGroup,
      idType: option.idType,
      description: detailDescription
    });
    this.setState({
      detailLetter: null,
      detailStudent: null,
      detailType: "",
      detailDescription: "",
      letters
    });
  };

  getInsertData = (occurrences, absences, schedule) => {
    const { students } = this.state;

    let oldOccurrences = students.map(std =>
      (std.occurrences || []).map(occur => ({
        studentId: std.id,
        ...occur
      }))
    );
    oldOccurrences = _.flatten(oldOccurrences);

    const oldAbsences = students.map(std => ({
      studentId: std.id,
      value: std.absence ? "A" : "P"
    }));

    const removedOccurrences = _.differenceWith(
      oldOccurrences,
      occurrences,
      (a, b) =>
        a.studentId === b.studentId &&
        String(a.idGroup) === String(b.idGroup) &&
        String(a.idType) === String(b.idType)
    );
    const newOccurrences = _.differenceWith(
      occurrences,
      oldOccurrences,
      (a, b) =>
        a.studentId === b.studentId &&
        String(a.idGroup) === String(b.idGroup) &&
        String(a.idType) === String(b.idType)
    );
    const newAbsences = _.differenceWith(
      absences,
      oldAbsences,
      (a, b) => a.value === b.value && a.studentId === b.studentId
    );

    return {
      idTurmaDisc: schedule.idTurmaDisc,
      idPerlet: schedule.idPerLet,
      date: this.formatSelectedDate(schedule),
      scheduleId: schedule.scheduleId,
      occurrences: newOccurrences,
      removed: removedOccurrences,
      absences: newAbsences
    };
  };

  buildSavePayload = () => {
    const { students, marks, letters } = this.state;
    const schedule = this.getSelectedSchedule();
    if (!schedule) return null;

    const occurrences = [];
    const absences = [];

    students.forEach(student => {
      const studentMarks = marks[student.id] || {};
      (student.occurrences || []).forEach(occur => {
        const isRefo = letters.some(letter =>
          occurrenceMatchesLetter(occur, letter)
        );
        if (!isRefo) {
          occurrences.push({
            studentId: student.id,
            idGroup: occur.idGroup,
            idType: occur.idType,
            description: occur.description || ""
          });
        }
      });
      letters.forEach(letter => {
        if (letter.kind !== "occurrence") return;
        const mark = studentMarks[letter.letter];
        if (!mark) return;
        occurrences.push({
          studentId: student.id,
          idGroup: mark.idGroup,
          idType: mark.idType,
          description: mark.description || ""
        });
      });
      absences.push({
        studentId: student.id,
        idTurmaDisc: student.idTurmaDisc || schedule.idTurmaDisc,
        idSchedule: student.idSchedule || schedule.scheduleId,
        value: studentMarks.A ? "A" : "P"
      });
    });

    return this.getInsertData(occurrences, absences, schedule);
  };

  save = async () => {
    const { alert, t } = this.props;
    const { classId } = this.state;
    const schedule = this.getSelectedSchedule();
    const data = this.buildSavePayload();

    if (
      !data ||
      !(
        (data.occurrences && data.occurrences.length) ||
        (data.removed && data.removed.length) ||
        (data.absences && data.absences.length)
      )
    ) {
      alert.show(t("occurrences.noChanges"), {
        title: t("common.actions.attention")
      });
      return;
    }

    this.setState({ loadingSave: true });
    try {
      const date = this.formatSelectedDate(schedule);
      await TeacherService.saveOccurrence(data, {
        classId,
        filter: {
          course: { id: schedule.idTurmaDisc },
          period: date
        }
      });

      alert.show(t("common.messages.processingRequest"), {
        title: t("occurrences.refo.sent")
      });

      const pendingJobKey = this.getPendingOccurrenceJobKey(classId, schedule);
      this.setState(
        prevState => ({
          dirty: false,
          loadingJobs:
            pendingJobKey && !prevState.loadingJobs.includes(pendingJobKey)
              ? [...prevState.loadingJobs, pendingJobKey]
              : prevState.loadingJobs,
          loadingMessage: t("occurrences.refo.jobInProgress")
        }),
        () => {
          fetchGradeJobQueue();
        }
      );
    } catch (e) {
      const message = _.get(
        e,
        "response.data.error.message",
        t("occurrences.saveError")
      );
      alert.show(message, { title: t("common.actions.error") });
    } finally {
      this.setState({ loadingSave: false });
    }
  };

  letterTooltip = letter => {
    const { t } = this.props;
    const label = t(LETTER_LABEL_KEYS[letter.letter]);
    if (letter.kind === "unavailable") {
      return `${label} — ${t("occurrences.refo.unavailable")}`;
    }
    return label;
  };

  render() {
    const { t } = this.props;
    const { context: ability } = this;
    const {
      classId,
      filter,
      loadingSave,
      loadingSchedules,
      schedulesError,
      loadingOccurrences,
      students,
      schedules,
      letters,
      marks,
      dirty,
      loadingMessage,
      selectedLessonDate,
      detailLetter,
      detailStudent,
      detailType,
      detailDescription,
      chromeStuck,
      chromeHeight,
      chromeBox,
      className
    } = this.state;

    const isOccurrenceJobPending = this.isJobPending();
    const hasNoLessons =
      !loadingSchedules && !schedulesError && schedules.length === 0;
    const resolvedLetters = letters.length ? letters : mergeRefoCatalog([]);

    return (
      <div>
        <Grid container spacing={2}>
          <BackButton to={`/classes/${classId}`} />
        </Grid>
        <StickyScope>
          <StickySentinel ref={this.setSentinelRef} />
          {chromeStuck && <StickyPlaceholder $height={chromeHeight} />}
          <StickyChrome
            ref={this.setChromeRef}
            $stuck={chromeStuck}
            $box={chromeBox}
          >
            {loadingSchedules ? (
              <LoadContainer>
                <CircularProgress size={28} />
              </LoadContainer>
            ) : (
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <LessonFilterRow>
                  {chromeStuck && (
                    <Breadcrumb aria-label={t("shell.breadcrumb.label")}>
                      <CrumbLink to="/classes">
                        {lastTitleSegment(t("shell.route.classes"))}
                      </CrumbLink>
                      <CrumbSep>&gt;</CrumbSep>
                      <CrumbLink to={`/classes/${classId}`}>
                        {className || classId}
                      </CrumbLink>
                      <CrumbSep>&gt;</CrumbSep>
                      <CrumbCurrent>
                        {lastTitleSegment(t("shell.route.classRefo"))}
                      </CrumbCurrent>
                    </Breadcrumb>
                  )}
                  <FilterFields>
                    <DatePickerField
                      disableToolbar
                      variant="inline"
                      format="dd/MM/yyyy"
                      margin="none"
                      label={t("common.labels.date")}
                      value={selectedLessonDate}
                      onChange={this.handleScheduleDateChange}
                      maxDate={new Date()}
                      disabled={loadingSchedules || isOccurrenceJobPending}
                    />
                    <LessonField>
                      <InputLabel id="refo-filter-lessons">
                        {t("occurrences.refo.lesson")}
                      </InputLabel>
                      <Select
                        fullWidth
                        labelId="refo-filter-lessons"
                        value={filter.lesson}
                        onChange={e => {
                          this.changeSchedule(e.target.value);
                          this.setState({
                            filter: { lesson: e.target.value }
                          });
                        }}
                        disabled={loadingSchedules || isOccurrenceJobPending}
                      >
                        {schedules.map(lesson => (
                          <MenuItem key={lesson.id} value={lesson.id}>
                            {this.formatLessonOption(lesson)}
                          </MenuItem>
                        ))}
                      </Select>
                    </LessonField>
                  </FilterFields>
                  {!hasNoLessons && (
                    <Can I="save" a="refo">
                      <CompactSave
                        color="primary"
                        variant="contained"
                        disabled={
                          !dirty || loadingSave || isOccurrenceJobPending
                        }
                        onClick={() => this.save()}
                      >
                        {t("common.actions.save")}
                      </CompactSave>
                    </Can>
                  )}
                </LessonFilterRow>
              </MuiPickersUtilsProvider>
            )}
            <LegendBar>
              {resolvedLetters.map(letter => (
                <LegendItem key={letter.letter}>
                  <LegendLetter $color={REFO_LETTER_COLORS[letter.letter]}>
                    {letter.letter}
                  </LegendLetter>
                  {t(LETTER_LABEL_KEYS[letter.letter])}
                </LegendItem>
              ))}
            </LegendBar>
          </StickyChrome>
          <Group>
            <Box mt={1}>
              {hasNoLessons && (
                <Typography color="textSecondary" align="center">
                  {t("occurrences.noLessonsOccurrence.body")}
                </Typography>
              )}
              {loadingOccurrences || isOccurrenceJobPending ? (
                <JobInProgressLoading
                  inProgress={isOccurrenceJobPending}
                  message={
                    loadingMessage || t("occurrences.refo.jobInProgress")
                  }
                />
              ) : (
                <Album>
                  {students.map(student => {
                    const studentMarks = marks[student.id] || {};
                    const ra = student.ra || student.id;
                    const canOpenProfile =
                      ability &&
                      ability.can("use module", "diarySpecificStudent");
                    return (
                      <StudentCard key={student.id}>
                        <PhotoRow>
                          <Portrait
                            as={canOpenProfile ? "button" : "div"}
                            type={canOpenProfile ? "button" : undefined}
                            $clickable={canOpenProfile}
                            aria-label={
                              canOpenProfile ? student.name : undefined
                            }
                            onClick={
                              canOpenProfile
                                ? () => this.openStudentProfile(student)
                                : undefined
                            }
                          >
                            {student.photo ? (
                              <img
                                src={student.photo}
                                alt={canOpenProfile ? "" : student.name}
                              />
                            ) : (
                              <Initials>
                                {this.studentInitials(student.name)}
                              </Initials>
                            )}
                          </Portrait>
                          <LetterCol>
                            {resolvedLetters.map(letter => (
                              <li key={letter.letter}>
                                <Tooltip title={this.letterTooltip(letter)}>
                                  <span>
                                    <LetterButton
                                      type="button"
                                      $active={!!studentMarks[letter.letter]}
                                      $color={REFO_LETTER_COLORS[letter.letter]}
                                      $unavailable={
                                        letter.kind === "unavailable"
                                      }
                                      disabled={
                                        isOccurrenceJobPending ||
                                        letter.kind === "unavailable"
                                      }
                                      onClick={() =>
                                        this.toggleLetter(student, letter)}
                                    >
                                      {letter.letter}
                                    </LetterButton>
                                  </span>
                                </Tooltip>
                              </li>
                            ))}
                          </LetterCol>
                        </PhotoRow>
                        <StudentRa>
                          {`Nº ${student.rollCallNum || ""} - RA: ${ra}`}
                        </StudentRa>
                        <StudentName>{student.name}</StudentName>
                      </StudentCard>
                    );
                  })}
                </Album>
              )}
            </Box>
          </Group>
        </StickyScope>

        <Dialog
          open={!!detailLetter}
          onClose={() =>
            this.setState({
              detailLetter: null,
              detailStudent: null
            })}
        >
          <DialogTitle>
            {detailStudent
              ? t("occurrences.refo.detailTitle", {
                  name: detailStudent.name
                })
              : t("occurrences.refo.title")}
          </DialogTitle>
          <DialogContent>
            {detailStudent && (
              <Box mb={2}>
                <Typography>{detailStudent.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("occurrences.refo.ra", {
                    ra: detailStudent.ra || detailStudent.id
                  })}
                </Typography>
              </Box>
            )}
            <FormControl fullWidth>
              <InputLabel id="refo-detail-type">
                {t("occurrences.refo.occurrenceType")}
              </InputLabel>
              <Select
                labelId="refo-detail-type"
                value={detailType}
                onChange={e => this.setState({ detailType: e.target.value })}
              >
                {(detailLetter && detailLetter.options
                  ? detailLetter.options
                  : []
                ).map(option => (
                  <MenuItem
                    key={`${option.idType}-${option.idGroup}`}
                    value={`${option.idType}-${option.idGroup}`}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box mt={2}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("occurrences.refo.occurrence")}
                value={detailDescription}
                onChange={e =>
                  this.setState({ detailDescription: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              color="default"
              onClick={() =>
                this.setState({
                  detailLetter: null,
                  detailStudent: null
                })}
            >
              {t("common.actions.cancel")}
            </Button>
            {detailStudent &&
              detailLetter &&
              marks[detailStudent.id] &&
              marks[detailStudent.id][detailLetter.letter] && (
                <Button
                  color="default"
                  onClick={() => {
                    this.setMark(detailStudent.id, detailLetter.letter, false);
                    this.setState({
                      detailLetter: null,
                      detailStudent: null
                    });
                  }}
                >
                  {t("common.actions.delete")}
                </Button>
              )}
            <Button
              color="primary"
              variant="contained"
              onClick={this.confirmDetail}
            >
              {t("common.actions.confirm")}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

RefoGrid.contextType = AbilityContext;

RefoGrid.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  jobsState: PropTypes.shape({
    jobs: PropTypes.arrayOf(PropTypes.shape())
  }).isRequired,
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  jobsState: state.jobsState
});

export default withTranslation()(
  withAlert()(withRouter(connect(mapStateToProps)(RefoGrid)))
);
